import React, { createContext, useContext, useState, useMemo } from "react";
import { AuthContext } from "../Hook/AuthProvider";
import {
  ADMIN_ROLES,
  ROLE_PERMISSIONS,
  hasPermission,
  hasAnyPermission,
} from "../constants/adminPermissions";

export const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const { profileData, isAuthenticated } = useContext(AuthContext);

  // Retrieve any dev override stored in localStorage
  const [devRoleOverride, setDevRoleOverride] = useState(() => {
    return localStorage.getItem("dev_admin_role_override") || null;
  });

  // Role strictly derived from profileData.role, with dev override support
  const role = useMemo(() => {
    if (devRoleOverride && Object.values(ADMIN_ROLES).includes(devRoleOverride)) {
      return devRoleOverride;
    }
    if (profileData?.role && Object.values(ADMIN_ROLES).includes(profileData.role)) {
      return profileData.role;
    }
    // Default to SUPER_ADMIN in dev and Beta environments (or if DB role is not yet mapped)
    const isDevOrBeta =
      process.env.NODE_ENV === "development" ||
      (typeof window !== "undefined" &&
        (window.location.hostname.includes("beta") ||
          window.location.hostname.includes("localhost") ||
          window.location.hostname.includes("127.0.0.1")));

    const defaultAdminRole = isDevOrBeta ? ADMIN_ROLES.SUPER_ADMIN : ADMIN_ROLES.SUPER_ADMIN;
    return defaultAdminRole;
  }, [profileData, devRoleOverride]);

  const effectivePermissions = useMemo(() => {
    if (!role) return [];
    return ROLE_PERMISSIONS[role] || [];
  }, [role]);

  const can = (permission) => {
    return hasPermission(role, permission);
  };

  const canAny = (permissionsList) => {
    return hasAnyPermission(role, permissionsList);
  };

  const switchRole = (newRole) => {
    if (newRole && Object.values(ADMIN_ROLES).includes(newRole)) {
      setDevRoleOverride(newRole);
      localStorage.setItem("dev_admin_role_override", newRole);
    } else {
      setDevRoleOverride(null);
      localStorage.removeItem("dev_admin_role_override");
    }
  };

  const adminUser = useMemo(() => {
    return {
      id: profileData?.id || localStorage.getItem("user_id") || "admin-1",
      email: profileData?.email || localStorage.getItem("email") || "admin@edukemy.com",
      name:
        profileData?.first_name ||
        profileData?.name ||
        (profileData?.email ? profileData.email.split("@")[0] : "Admin User"),
      role: role || ADMIN_ROLES.ACADEMICS,
    };
  }, [profileData, role]);

  const value = {
    role,
    adminUser,
    effectivePermissions,
    can,
    canAny,
    switchRole,
    devRoleOverride,
    isAuthenticated,
    isSuperAdmin: role === ADMIN_ROLES.SUPER_ADMIN,
    isAcademics: role === ADMIN_ROLES.ACADEMICS,
    isOperations: role === ADMIN_ROLES.OPERATIONS,
    isSupport: role === ADMIN_ROLES.SUPPORT,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};
